import * as React from 'react';
import { InputLabel as MuiInputLabel, InputLabelProps as MuiInputLabelProps, styled } from '@mui/material';
import SelectBaseV2, { SelectBaseV2Props } from './SelectBaseV2';
import useId from '@mui/utils/useId';

const StyledSelect = styled('div')({
});

const SelectWrapper = styled('div')({
  height: '40px',
  width: '100%',
  borderRadius: '30px',
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  transition: 'background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  display: 'flex',
  alignItems: 'stretch',

  '&:not(.Mui-disabled)': {
    '&:hover, &:focus-within': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },

  '&& .MuiSelect-select.MuiSelect-standard': {
    display: 'block',
    lineHeight: '40px',
    overflow: 'hidden',
    paddingLeft: '16px',
    paddingRight: '44px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '&& .MuiSelect-select.MuiSelect-standard > *': {
    alignItems: 'center',
    display: 'inline-flex',
    height: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },

  '&& .MuiSelect-select.MuiSelect-standard > * > svg': {
    flexShrink: 0,
  },

  '&& .MuiSelect-select.MuiSelect-standard > * > span': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '.MuiInputBase-root': {
    cursor: 'pointer',
  },

  '.MuiSelect-icon': {
    right: '12px',
  }
});

export type SelectV2Props = React.PropsWithChildren<{
  id?: string
  label?: React.ReactNode
  value: unknown
  onChange: NonNullable<SelectBaseV2Props['onChange']> | ((e: React.ChangeEvent<HTMLInputElement>) => void)
  disabled?: boolean,
  error?: boolean
  InputLabelProps?: Omit<MuiInputLabelProps, "children" | "ref">
  SelectBaseProps?: Omit<SelectBaseV2Props, "children" | "ref">
}>

export default function SelectV2(props: SelectV2Props) {
  const id = useId(props.id)
  const [opened, setOpened] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  const { SelectBaseProps, InputLabelProps, children } = props;
  const { onBlur, onClose, onFocus, onOpen, ...restSelectBaseProps } = SelectBaseProps ?? {};

  return <StyledSelect>
    {props.label ? <MuiInputLabel
      htmlFor={id}
      error={props.error}
      disabled={props.disabled}
      focused={focused || opened}
      {...InputLabelProps}
    >{props.label}</MuiInputLabel> : <></>}
    <SelectWrapper className={props.disabled ? 'Mui-disabled' : undefined}>
      <SelectBaseV2
        error={props.error}
        id={id}
        value={props.value}
        onChange={props.onChange as SelectBaseV2Props['onChange']}
        disabled={props.disabled}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onClose={(event) => {
          setOpened(false);
          onClose?.(event);
        }}
        onOpen={(event) => {
          setOpened(true);
          onOpen?.(event);
        }}
        {...restSelectBaseProps}
      >{children}</SelectBaseV2>
    </SelectWrapper>
  </StyledSelect>
}
